/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ExpenseFilters } from "~/types/expense";

// Types for filter context
export interface FilterContextValue<T> {
  filters: T;
  updateFilters: (updates: Partial<T>) => void;
  resetFilters: () => void;
  setFilters: (filters: T) => void;
}

// Expense filters context
const ExpenseFiltersContext =
  createContext<FilterContextValue<ExpenseFilters> | null>(null);

// Expense filters provider component
export interface ExpenseFiltersProviderProps {
  children: React.ReactNode;
  initialFilters?: Partial<ExpenseFilters>;
  isFixed: boolean; // Required to determine if it's fixed or variable expenses
}

export function ExpenseFiltersProvider({
  children,
  initialFilters = {},
  isFixed,
}: ExpenseFiltersProviderProps) {
  const defaultFilters: ExpenseFilters = useMemo(
    () => ({
      isFixed,
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      page: 1,
      limit: 15,
      ...initialFilters,
    }),
    [isFixed, initialFilters],
  );

  const [filters, setFiltersState] = useState<ExpenseFilters>(defaultFilters);

  const updateFilters = useCallback((updates: Partial<ExpenseFilters>) => {
    setFiltersState((prev) => {
      // Reset page to 1 when month or year changes
      if (updates.month !== undefined || updates.year !== undefined) {
        return { ...prev, ...updates, page: 1 };
      }
      return { ...prev, ...updates };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters);
  }, [defaultFilters]);

  const setFilters = useCallback((newFilters: ExpenseFilters) => {
    setFiltersState(newFilters);
  }, []);

  const value: FilterContextValue<ExpenseFilters> = {
    filters,
    updateFilters,
    resetFilters,
    setFilters,
  };

  return (
    <ExpenseFiltersContext.Provider value={value}>
      {children}
    </ExpenseFiltersContext.Provider>
  );
}

// Hook to use expense filters
export function useExpenseFilters() {
  const context = useContext(ExpenseFiltersContext);

  if (!context) {
    throw new Error(
      "useExpenseFilters must be used within an ExpenseFiltersProvider",
    );
  }

  return context;
}
